import React from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import Option from "@mui/joy/Option";
import Select from "@mui/joy/Select";
import Table from "@mui/joy/Table";
import Typography from "@mui/joy/Typography";
import { BarChart } from "@mui/x-charts/BarChart";
import {
    AnalyticsCard,
    BlockError,
    BlockLoading,
    EmptyState,
    MetricHint,
    ScrollTable,
} from "../../bids/analytics/components/AnalyticsShared";
import { useAsyncBlock } from "../../bids/analytics/hooks";
import {
    formatHours,
    formatInt,
    formatMoneyKopecks,
    formatRatePercent,
    formatUtcLocalShort,
    toNum,
} from "../../bids/analytics/format";
import { PromoCodeUsagesTable } from "../../adCampaigns/PromoCodeUsagesTable";
import { TermHint } from "../components/TermHint";
import { adCampaignPromoCodeUsagesApi } from "../../adCampaigns/adCampaignsApi";
import {
    PromoBonusType,
    PromoBonusTypeLabels,
    UserPromoCodeStatus,
    UserPromoCodeStatusLabels,
} from "../../../models/promoCode";
import { ueCampaignPromoApi } from "../unitEconomicsApi";

const USAGES_PAGE_SIZE = 20;

const metricGridSx = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
    gap: 1,
};

type Props = {
    campaignId: number;
    isActive: boolean;
};

/** Вкладка «Промокод» детализации кампании: воронка, бонусы по типам, динамика и использования. */
export function CampaignPromoTab({ campaignId, isActive }: Props) {
    const [state, refresh] = useAsyncBlock(() => ueCampaignPromoApi(campaignId), isActive, [campaignId]);
    const d = state.data;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button size="sm" variant="outlined" loading={state.loading} onClick={refresh}>
                    Обновить
                </Button>
            </Box>
            {state.loading && !d && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {d && !d.promoCode && d.activations === 0 && (
                <EmptyState hint="У кампании нет промокода. Задайте его и бонусы в редакторе кампании." />
            )}
            {d && (d.promoCode || d.activations > 0) && (
                <>
                    <AnalyticsCard
                        title={`Воронка промокода ${d.promoCode ?? ""}`.trim()}
                        subtitle="Активации за срок кампании по текущему статусу."
                        snapshotLabel="lifetime"
                        howToRead={
                            <List size="sm" marker="disc">
                                <ListItem>
                                    <Typography level="body-sm">
                                        <strong>Пример:</strong> промокод ввели 50 человек. У 30 бонус уже сработал
                                        (израсходованы), 12 ещё ждут покупки (активны), 5 ввели другой промокод
                                        (заменены), у 3 кампания закончилась раньше (истекли). Конверсия 30 ÷ 50 =
                                        60%.
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography level="body-sm">
                                        <strong>Лимит</strong> — активные и израсходованные за всё время к лимиту
                                        кампании: 12 + 30 = 42 из 100. Заменённые и истёкшие места не занимают.
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography level="body-sm">
                                        <strong>До применения</strong> — сколько в среднем проходит от ввода
                                        промокода до первого события с бонусом: ввёл в 10:00, купил в 14:00 — 4
                                        часа.
                                    </Typography>
                                </ListItem>
                            </List>
                        }
                    >
                        <Box sx={metricGridSx}>
                            <MetricHint label={<TermHint term="campPromoActivations" />} value={formatInt(d.activations)} />
                            <MetricHint label={<TermHint term="campPromoActive" />} value={formatInt(d.active)} />
                            <MetricHint
                                label={<TermHint term="campPromoRedeemed" />}
                                value={formatInt(d.redeemed)}
                                color="success"
                            />
                            <MetricHint label={<TermHint term="campPromoReplaced" />} value={formatInt(d.replaced)} />
                            <MetricHint label={<TermHint term="campPromoExpired" />} value={formatInt(d.expired)} />
                            <MetricHint
                                label={<TermHint term="campPromoConversion" />}
                                value={formatRatePercent(d.redemptionRate)}
                                color="primary"
                            />
                            <MetricHint
                                label={<TermHint term="campPromoLimit" />}
                                value={
                                    d.maxActivations != null
                                        ? `${formatInt(d.limitUsed)} / ${formatInt(d.maxActivations)}`
                                        : `${formatInt(d.limitUsed)} / ∞`
                                }
                            />
                            <MetricHint
                                label={<TermHint term="campPromoLimitFill" />}
                                value={d.limitFillRate != null ? formatRatePercent(d.limitFillRate) : "—"}
                            />
                            <MetricHint
                                label={<TermHint term="campPromoTimeToRedeem" />}
                                value={formatHours(d.avgHoursToRedeem, d.redeemed)}
                            />
                        </Box>
                    </AnalyticsCard>

                    <AnalyticsCard
                        title="Бонусы по типам"
                        subtitle="Применения за срок кампании: оборот операций и наши затраты."
                        snapshotLabel="lifetime"
                        howToRead={
                            <List size="sm" marker="disc">
                                <ListItem>
                                    <Typography level="body-sm">
                                        <strong>Оборот</strong> — сумма заказов или пополнений, к которым применился
                                        бонус: 20 покупок по 100 ₽ — 2 000 ₽.
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Typography level="body-sm">
                                        <strong>Затраты</strong> — сколько мы отдали: недобор комиссии с продавца,
                                        скидка покупателю или бонус к пополнению. Скидка 1% с 2 000 ₽ — 20 ₽, в
                                        среднем 1 ₽ на применение.
                                    </Typography>
                                </ListItem>
                            </List>
                        }
                    >
                        <ScrollTable>
                            <Table size="sm">
                                <thead>
                                    <tr>
                                        <th>Бонус</th>
                                        <th style={{ textAlign: "right" }}>
                                            <TermHint term="campBonusRedemptions" />
                                        </th>
                                        <th style={{ textAlign: "right" }}>
                                            <TermHint term="campBonusTurnover" />
                                        </th>
                                        <th style={{ textAlign: "right" }}>
                                            <TermHint term="campBonusCost" />
                                        </th>
                                        <th style={{ textAlign: "right" }}>
                                            <TermHint term="campBonusAvgCost" />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {d.byBonusType.map((b) => (
                                        <tr key={b.bonusType}>
                                            <td>{PromoBonusTypeLabels[b.bonusType]}</td>
                                            <td style={{ textAlign: "right" }}>{formatInt(b.redemptions)}</td>
                                            <td style={{ textAlign: "right" }}>
                                                {formatMoneyKopecks(b.turnoverKopecks)}
                                            </td>
                                            <td style={{ textAlign: "right" }}>{formatMoneyKopecks(b.costKopecks)}</td>
                                            <td style={{ textAlign: "right" }}>
                                                {b.redemptions > 0 ? formatMoneyKopecks(b.avgCostKopecks) : "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </ScrollTable>
                    </AnalyticsCard>

                    <AnalyticsCard
                        title="Динамика по дням"
                        subtitle="Активации — по дате активации, применения — по дате срабатывания бонуса."
                        snapshotLabel="lifetime"
                        howToRead={
                            <Typography level="body-sm">
                                Дни без событий не показываются. Столбцы применений разбиты по типу бонуса.
                            </Typography>
                        }
                    >
                        {d.daily.length === 0 ? (
                            <EmptyState hint="Пока нет активаций." />
                        ) : (
                            <Box sx={{ width: "100%", minWidth: 0 }}>
                                <BarChart
                                    height={240}
                                    series={[
                                        { data: d.daily.map((p) => toNum(p.activations)), label: "Активации" },
                                        {
                                            data: d.daily.map((p) => toNum(p.saleRedemptions)),
                                            label: PromoBonusTypeLabels[PromoBonusType.Sale],
                                            stack: "redeemed",
                                        },
                                        {
                                            data: d.daily.map((p) => toNum(p.purchaseRedemptions)),
                                            label: PromoBonusTypeLabels[PromoBonusType.Purchase],
                                            stack: "redeemed",
                                        },
                                        {
                                            data: d.daily.map((p) => toNum(p.depositRedemptions)),
                                            label: PromoBonusTypeLabels[PromoBonusType.Deposit],
                                            stack: "redeemed",
                                        },
                                    ]}
                                    xAxis={[
                                        {
                                            data: d.daily.map((p) => formatUtcLocalShort(p.dayUtc)),
                                            scaleType: "band",
                                        },
                                    ]}
                                    margin={{ left: 40, right: 12, top: 48, bottom: 40 }}
                                />
                            </Box>
                        )}
                    </AnalyticsCard>

                    <PromoUsagesCard campaignId={campaignId} isActive={isActive} />
                </>
            )}
        </Box>
    );
}

function PromoUsagesCard({ campaignId, isActive }: Props) {
    const [page, setPage] = React.useState(1);
    const [status, setStatus] = React.useState<UserPromoCodeStatus | null>(null);
    const [bonusType, setBonusType] = React.useState<PromoBonusType | null>(null);
    const [state, refresh] = useAsyncBlock(
        () =>
            adCampaignPromoCodeUsagesApi({
                page,
                count: USAGES_PAGE_SIZE,
                campaignId,
                status: status ?? undefined,
                bonusType: bonusType ?? undefined,
            }),
        isActive,
        [campaignId, page, status, bonusType]
    );
    const d = state.data;
    const pageCount = d ? Math.max(1, Math.ceil(d.total / USAGES_PAGE_SIZE)) : 1;

    return (
        <AnalyticsCard
            title="Использования"
            subtitle="Кто активировал промокод, на каких условиях, что сработало и во что обошлось."
            snapshotLabel="now"
            howToRead={
                <Typography level="body-sm">
                    Условия зафиксированы при активации: правка бонусов кампании их не меняет. Операция — заказ или
                    депозит, на который израсходован промокод.
                </Typography>
            }
        >
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "flex-end" }}>
                <FormControl size="sm" sx={{ minWidth: 160 }}>
                    <FormLabel>Статус</FormLabel>
                    <Select
                        value={status}
                        placeholder="Все"
                        onChange={(_, v) => {
                            setStatus(v);
                            setPage(1);
                        }}
                    >
                        <Option value={null}>Все</Option>
                        {Object.values(UserPromoCodeStatus)
                            .filter((v): v is UserPromoCodeStatus => typeof v === "number")
                            .map((v) => (
                                <Option key={v} value={v}>
                                    {UserPromoCodeStatusLabels[v]}
                                </Option>
                            ))}
                    </Select>
                </FormControl>
                <FormControl size="sm" sx={{ minWidth: 200 }}>
                    <FormLabel>Сработал бонус</FormLabel>
                    <Select
                        value={bonusType}
                        placeholder="Любой"
                        onChange={(_, v) => {
                            setBonusType(v);
                            setPage(1);
                        }}
                    >
                        <Option value={null}>Любой</Option>
                        {Object.values(PromoBonusType)
                            .filter((v): v is PromoBonusType => typeof v === "number")
                            .map((v) => (
                                <Option key={v} value={v}>
                                    {PromoBonusTypeLabels[v]}
                                </Option>
                            ))}
                    </Select>
                </FormControl>
                <Button size="sm" variant="outlined" loading={state.loading} onClick={refresh}>
                    Обновить
                </Button>
            </Box>
            {state.loading && !d && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {d && d.items.length === 0 && <EmptyState hint="Нет активаций с такими фильтрами." />}
            {d && d.items.length > 0 && (
                <>
                    <PromoCodeUsagesTable items={d.items} showCampaign={false} />
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "flex-end" }}>
                        <Button size="sm" variant="plain" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                            ← Назад
                        </Button>
                        <Typography level="body-sm">
                            {page} / {pageCount} · всего {formatInt(d.total)}
                        </Typography>
                        <Button
                            size="sm"
                            variant="plain"
                            disabled={page >= pageCount}
                            onClick={() => setPage(page + 1)}
                        >
                            Вперёд →
                        </Button>
                    </Box>
                </>
            )}
        </AnalyticsCard>
    );
}
